import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { trackSponsorClick } from "@/lib/sponsor";

const HIDDEN_ON = ["/login", "/registrati", "/password-dimenticata", "/reset-password", "/chat"];

export function RecommendedBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [items, setItems] = useState([]);
  const track = useRef(null);
  const paused = useRef(false);

  const isBuyer = !user || ["public_entity", "business", "citizen"].includes(user.role);
  const visible = isBuyer && !HIDDEN_ON.some((p) => pathname.startsWith(p));
  useEffect(() => {
    if (!visible) return;
    api.get("/recommended", { params: { track: true } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user?.id, visible]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = (d) => {
    const el = track.current;
    if (!el) { return; }
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    if (d > 0 && atEnd) { el.scrollTo({ left: 0, behavior: "smooth" }); return; }
    if (d < 0 && el.scrollLeft <= 0) { el.scrollTo({ left: el.scrollWidth, behavior: "smooth" }); return; }
    el.scrollBy({ left: d * 220, behavior: "smooth" });
  };
  useEffect(() => {
    if (!items.length) { return; }
    const t = setInterval(() => { if (!paused.current) { step(1); } }, 4000);
    return () => clearInterval(t);
  }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = visible && items.length > 0;
  useEffect(() => {
    document.body.classList.toggle("rec-bar-on", shown);
    return () => document.body.classList.remove("rec-bar-on");
  }, [shown]);

  if (!visible || items.length === 0) return null;
  const Arrow = ({ d, Icon, id }) => (
    <button type="button" onClick={() => step(d)} data-testid={`recommended-bar-${id}`} aria-label={d < 0 ? "Precedente" : "Successivo"}
      className="shrink-0 w-8 h-8 rounded-full border border-gold/50 text-gold flex items-center justify-center hover:bg-gold hover:text-[#0D2B5A] transition-colors"><Icon className="w-4 h-4"/></button>
  );

  return (
    <>
    <div className="h-16 bg-navy" aria-hidden="true" />
    <div className="fixed bottom-0 inset-x-0 z-30 bg-[#0D2B5A]/95 backdrop-blur-lg border-t border-gold/40 shadow-[0_-8px_30px_rgba(20,14,8,0.45)]" data-testid="recommended-bar">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <Link to="/consigliati" data-testid="recommended-bar-title" className="hidden sm:flex items-center gap-2 text-gold shrink-0 pr-3 border-r border-navy-700">
          <Sparkles className="w-4 h-4" />
          <span className="overline">Consigliati</span>
        </Link>
        <Arrow d={-1} Icon={ChevronLeft} id="prev"/>
        <div ref={track} onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }} onTouchStart={() => { paused.current = true; }} onTouchEnd={() => { setTimeout(() => { paused.current = false; }, 3000); }}
          className="flex-1 flex gap-2 overflow-x-auto snap-x scroll-smooth" style={{ scrollbarWidth: "none" }} data-testid="recommended-bar-track">
          {items.map((a) => (
            <Link key={a.id} to={`/artista/${a.id}`} onClick={() => trackSponsorClick(a)} data-testid={`recommended-bar-item-${a.id}`} className="snap-start flex items-center gap-2 shrink-0 bg-navy-800 hover:bg-navy-700 border border-navy-700 hover:border-gold/60 rounded-full pl-1 pr-3 py-1 transition-colors">
              <img src={mediaUrl(a.media?.photos?.[0]) || "/logo-icon.png"} alt="" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-white text-xs font-semibold whitespace-nowrap max-w-[160px] truncate">{a.companyName}</span>
              {a.rating ? <span className="text-gold text-xs flex items-center gap-0.5"><Star className="w-3 h-3 fill-gold" />{a.rating.toFixed(1)}</span> : null}
              <span className="text-[10px] uppercase tracking-wider text-slate-300">{a.sponsored ? <b className="text-gold">Sponsorizzato</b> : a.role === "supplier" ? "Fornitore" : "Artista"}</span>
            </Link>
          ))}
        </div>
        <Arrow d={1} Icon={ChevronRight} id="next"/>
      </div>
    </div>
    </>
  );
}
